import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SEMANTIC, UI } from '../../styles/theme';

const ACCENT = '#00ff88';

const STEPS = [
  { icon: '📅', label: 'Ogni lunedì mattina', delay: 0 },
  { icon: '🤖', label: 'L\'Autopilot si attiva', delay: 400 },
  { icon: '📊', label: 'Analizza i tuoi portafogli', delay: 800 },
  { icon: '💹', label: 'Esegue acquisti e vendite', delay: 1200 },
];

export default function AutopilotIntro() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState([]);
  const [pulseActive, setPulseActive] = useState(false);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    // Attiva il pulse dopo 300ms
    const t0 = setTimeout(() => setPulseActive(true), 300);
    // Mostra il contenuto dopo 600ms
    const t1 = setTimeout(() => setShowContent(true), 600);
    // Rivela ogni step in sequenza
    STEPS.forEach((s, i) => {
      setTimeout(() => {
        setVisible(v => [...v, i]);
      }, 900 + s.delay);
    });
    return () => { clearTimeout(t0); clearTimeout(t1); };
  }, []);

  return (
    <div style={{
      backgroundColor: SEMANTIC.backgroundApp,
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: UI.spacing.lg,
    }}>
      <div style={{ maxWidth: '560px', width: '100%', textAlign: 'center' }}>

        {/* Logo */}
        <img
          src={`${process.env.PUBLIC_URL}/Logo_Kuesto.png`}
          alt="Kuesto"
          style={{ width: '100px', marginBottom: '32px' }}
        />

        {/* Animated autopilot icon */}
        <div style={{ position: 'relative', display: 'inline-block', marginBottom: '32px' }}>
          {/* Pulse rings */}
          {[1, 2, 3].map(ring => (
            <div key={ring} style={{
              position: 'absolute',
              top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              width: `${80 + ring * 30}px`,
              height: `${80 + ring * 30}px`,
              borderRadius: '50%',
              border: `1px solid ${ACCENT}`,
              opacity: pulseActive ? 0 : 0.5,
              transition: pulseActive
                ? `opacity 1.5s ease-out ${ring * 0.3}s, transform 1.5s ease-out ${ring * 0.3}s`
                : 'none',
              animation: pulseActive ? `pulse-ring 2.5s ease-out infinite ${ring * 0.4}s` : 'none',
            }} />
          ))}
          {/* Central icon */}
          <div style={{
            width: '80px', height: '80px',
            borderRadius: '50%',
            backgroundColor: `${ACCENT}18`,
            border: `2px solid ${ACCENT}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '36px',
            boxShadow: pulseActive ? `0 0 24px ${ACCENT}55` : 'none',
            transition: 'box-shadow 0.8s ease',
            position: 'relative', zIndex: 1,
          }}>
            ✈️
          </div>
        </div>

        {/* Title */}
        <div style={{
          opacity: showContent ? 1 : 0,
          transform: showContent ? 'translateY(0)' : 'translateY(16px)',
          transition: 'opacity 0.6s ease, transform 0.6s ease',
        }}>
          <h2 style={{
            color: ACCENT,
            fontSize: '28px',
            fontWeight: 700,
            margin: '0 0 8px 0',
          }}>
            Autopilot attivato
          </h2>
          <p style={{
            color: SEMANTIC.textSecondary,
            fontSize: '15px',
            lineHeight: 1.6,
            margin: '0 0 32px 0',
          }}>
            D'ora in poi Kuesto gestirà i tuoi portafogli in modo completamente automatico.
            Non devi fare nulla: ci pensiamo noi.
          </p>
        </div>

        {/* Step list */}
        <div style={{
          backgroundColor: SEMANTIC.backgroundCard,
          borderRadius: UI.borderRadius.md,
          padding: '24px',
          marginBottom: '24px',
          textAlign: 'left',
        }}>
          {STEPS.map((s, i) => (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              padding: '10px 0',
              borderBottom: i < STEPS.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
              opacity: visible.includes(i) ? 1 : 0,
              transform: visible.includes(i) ? 'translateX(0)' : 'translateX(-16px)',
              transition: 'opacity 0.4s ease, transform 0.4s ease',
            }}>
              <div style={{
                width: '40px', height: '40px',
                borderRadius: '10px',
                backgroundColor: `${ACCENT}12`,
                border: `1px solid ${ACCENT}33`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '20px', flexShrink: 0,
              }}>
                {s.icon}
              </div>
              <span style={{ color: SEMANTIC.textPrimary, fontSize: '14px', fontWeight: 500 }}>
                {s.label}
              </span>
              {visible.includes(i) && (
                <span style={{
                  marginLeft: 'auto',
                  color: ACCENT,
                  fontSize: '16px',
                }}>✓</span>
              )}
            </div>
          ))}
        </div>

        {/* Mr. K note */}
        <div style={{
          backgroundColor: 'rgba(0,170,255,0.06)',
          border: '1px solid rgba(0,170,255,0.2)',
          borderRadius: UI.borderRadius.sm,
          padding: '14px 18px',
          marginBottom: '32px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          textAlign: 'left',
          opacity: showContent ? 1 : 0,
          transition: 'opacity 0.6s ease 0.3s',
        }}>
          <span style={{ fontSize: '24px', flexShrink: 0 }}>🧠</span>
          <div>
            <div style={{ color: '#00aaff', fontWeight: 700, fontSize: '13px', marginBottom: '4px' }}>
              Mr. K — Il tuo advisor AI
            </div>
            <div style={{ color: SEMANTIC.textSecondary, fontSize: '12px', lineHeight: 1.6 }}>
              Vuoi sapere perché è stato acquistato un titolo o venduto un altro?
              Chiedi a Mr. K: ti spiegherà ogni decisione con dati e ragionamenti chiari,
              in linguaggio semplice.
            </div>
          </div>
        </div>

        {/* CTA button */}
        <button
          onClick={() => navigate('/kyc/alpaca-onboarding')}
          style={{
            width: '100%',
            padding: UI.spacing.md,
            backgroundColor: ACCENT,
            color: '#000',
            border: 'none',
            borderRadius: UI.borderRadius.sm,
            fontSize: UI.fontSize.lg,
            fontWeight: UI.fontWeight.bold,
            cursor: 'pointer',
            transition: 'opacity 0.2s',
            opacity: visible.length === STEPS.length ? 1 : 0.5,
          }}
          onMouseOver={e => e.currentTarget.style.opacity = '0.9'}
          onMouseOut={e => e.currentTarget.style.opacity = visible.length === STEPS.length ? '1' : '0.5'}
        >
          Configura il conto Alpaca →
        </button>

        <style>{`
          @keyframes pulse-ring {
            0%   { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
            100% { transform: translate(-50%, -50%) scale(2.2); opacity: 0; }
          }
        `}</style>
      </div>
    </div>
  );
}
